import { parse } from "date-fns";
import Papa from "papaparse";
import { tz } from "@date-fns/tz";
import { BaseFermentationParser, } from "./parser-interface";
/**
 * Biostream parser implementing the new FermentationTimeseriesParser interface
 * Updated for Phase 3: Parser Migration
 */
export class BiostreamParser extends BaseFermentationParser {
    equipmentType = "Biostream";
    supportedFormats = [".csv", ".txt"];
    argsSchema = {
        ignoreMethanolZeroSetpoint: {
            type: "boolean",
            description: "If checked, the fermentation will not be considered ended when methanol setpoint reaches zero. This is useful when the fermentation continues after methanol feeding has stopped.",
            required: false,
            default: true,
        },
    };
    separator;
    dateTimeFormat;
    fileContents;
    constructor(fileContents) {
        super();
        this.fileContents = fileContents;
        if (fileContents) {
            this.initializeFormat(fileContents);
        }
    }
    decodeFile(buffer) {
        // Try UTF-8 first, fallback to ISO-8859-1 if replacement characters found
        let decoder = new TextDecoder("utf-8");
        let textContents = decoder.decode(buffer);
        if (textContents.includes("\uFFFD")) {
            decoder = new TextDecoder("iso-8859-1");
            textContents = decoder.decode(buffer);
        }
        // Normalize line endings
        return textContents.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    }
    initializeFormat(fileContents) {
        const semicolons = fileContents.startsWith("Date;Time;");
        const commas = fileContents.startsWith("Date,Time,");
        if (!semicolons && !commas) {
            throw new Error("Invalid Biostream file");
        }
        this.separator = semicolons ? ";" : ",";
        this.dateTimeFormat = this.guessDateTimeFormat(this.extractStartTime(fileContents))[0];
    }
    async validateFile(buffer) {
        try {
            const textContents = this.decodeFile(buffer);
            const errors = [];
            const warnings = [];
            // Check file format
            const semicolons = textContents.startsWith("Date;Time;");
            const commas = textContents.startsWith("Date,Time,");
            if (!semicolons && !commas) {
                errors.push("File does not start with expected Biostream header format");
            }
            // Check for required columns
            const requiredColumns = ["Date", "Time"];
            const firstLine = textContents.split("\n")[0];
            const separator = semicolons ? ";" : ",";
            const headers = firstLine.split(separator);
            for (const col of requiredColumns) {
                if (!headers.some((h) => h.toLowerCase() === col.toLowerCase())) {
                    errors.push(`Missing required column: ${col}`);
                }
            }
            // Test date parsing
            try {
                this.extractStartTime(textContents);
                this.guessDateTimeFormat(this.extractStartTime(textContents));
            }
            catch (error) {
                errors.push(`Date format validation failed: ${error}`);
            }
            return {
                isValid: errors.length === 0,
                errors,
                warnings,
            };
        }
        catch (error) {
            return {
                isValid: false,
                errors: [`File validation failed: ${error}`],
            };
        }
    }
    async extractMetadata(buffer) {
        const textContents = this.decodeFile(buffer);
        this.initializeFormat(textContents);
        const projectInfo = this.extractProjectInfo(textContents);
        // Last data row carries the file's end time. Scan backwards over the raw
        // text parsing one line at a time — parsing the whole file to read a single
        // value costs hundreds of MB on a large export.
        const endTime = await this.findLastRecordedAt(textContents);
        const durationSeconds = endTime
            ? (endTime.getTime() - projectInfo.startTime.getTime()) / 1000
            : undefined;
        return {
            equipmentType: this.equipmentType,
            startTime: projectInfo.startTime,
            endTime,
            durationSeconds,
            tankNumbers: [0], // Biostream files only contain one tank
        };
    }
    /**
     * Timestamp of the last row carrying a parseable Date/Time, found by
     * scanning backwards and parsing single lines. Bounded to the tail; falls
     * back to a full parse so pathological files keep the original semantics.
     */
    async findLastRecordedAt(textContents) {
        const headerEnd = textContents.indexOf("\n");
        if (headerEnd > 0) {
            const header = textContents.slice(0, headerEnd);
            let lineEnd = textContents.length;
            for (let scanned = 0; scanned < 1000; scanned++) {
                if (lineEnd <= headerEnd)
                    break;
                const lineStart = textContents.lastIndexOf("\n", lineEnd - 1) + 1;
                if (lineStart <= headerEnd)
                    break;
                const line = textContents.slice(lineStart, lineEnd).trim();
                lineEnd = lineStart - 1;
                if (!line)
                    continue;
                const [row] = await this.parseCsv(`${header}\n${line}`, this.separator);
                if (!row?.Date)
                    continue;
                const recordedAt = this.parseDateTime(`${row.Date} ${row.Time}`);
                if (!isNaN(recordedAt.getTime()))
                    return recordedAt;
            }
        }
        const rows = await this.parseCsv(textContents, this.separator);
        for (let i = rows.length - 1; i >= 0; i--) {
            if (!rows[i].Date)
                continue;
            const recordedAt = this.parseDateTime(`${rows[i].Date} ${rows[i].Time}`);
            if (!isNaN(recordedAt.getTime()))
                return recordedAt;
        }
        return undefined;
    }
    async parseData(buffer, fermentations, tankIdToFermIdMap, args, additionTotals) {
        return this.collectRecordings(this.parseRecordStream(buffer, fermentations, tankIdToFermIdMap, args, additionTotals));
    }
    /**
     * Streaming implementation. Always a single fermentation, so every record
     * carries the same id.
     *
     * The loop keeps mutable per-fermentation state across rows — the latched
     * start time, the previous timestamp, four running integrals and the
     * methanol-setpoint latch — which lives in the generator frame. Two orderings
     * are load-bearing and preserved exactly: `createRawData` is called *before*
     * the integrals are updated, so each row sees the integral excluding itself;
     * and a row with no `Date` ends the data rather than being skipped.
     */
    async *parseRecordStream(buffer, fermentations, _tankIdToFermIdMap, args, _additionTotals) {
        const textContents = this.decodeFile(buffer);
        this.initializeFormat(textContents);
        const ignoreMethanolZeroSetpoint = args?.ignoreMethanolZeroSetpoint ?? false;
        // Biostream parser assumes single fermentation like Applikon
        const fermentationId = fermentations[0].id;
        const rows = this.iterateDelimitedRows(textContents, 0, textContents.length, (chunk) => this.parseCsv(chunk, this.separator));
        let hadMethanolSetPointValue = false;
        let startTime = fermentations[0].started_at ?? null;
        let lastTimestamp = 0;
        let integratedAntifoam = 0;
        let integratedBase = 0;
        let integratedMethanol = 0;
        let integratedNutrients = 0;
        for await (const row of rows) {
            const methanolSetPointValue = this.toNumber(this.getVal(row, "Methanol feed (g/min)! Setpoint (mL/min)"));
            if (methanolSetPointValue !== null && methanolSetPointValue > 0) {
                hadMethanolSetPointValue = true;
            }
            if (hadMethanolSetPointValue &&
                !ignoreMethanolZeroSetpoint &&
                methanolSetPointValue === 0) {
                // Fermentation has ended
                break;
            }
            if (!row.Date) {
                break;
            }
            const dataTimeStr = `${row.Date} ${row.Time}`;
            const recordedAt = this.parseDateTime(dataTimeStr);
            if (isNaN(recordedAt.getTime())) {
                throw new Error(`Invalid date format: ${dataTimeStr}`);
            }
            if (!startTime) {
                startTime = recordedAt;
            }
            else if (recordedAt < startTime) {
                continue; // Skip rows before inoculation time
            }
            const timestamp_s = (recordedAt.getTime() - startTime.getTime()) / 1000;
            // Calculate time delta in minutes for integration
            const deltaMinutes = lastTimestamp === 0 ? 0 : (timestamp_s - lastTimestamp) / 60;
            lastTimestamp = timestamp_s;
            // Create raw data object for mapping
            const rawData = this.createRawData(row, deltaMinutes, {
                integratedAntifoam,
                integratedBase,
                integratedMethanol,
                integratedNutrients,
            });
            // Update integrated values
            const antifoam_value = this.toNumber(this.getVal(row, "Antifoam Value (V)"));
            integratedAntifoam += (antifoam_value ?? 0) * deltaMinutes;
            const base_value_g_min = this.hasVal(row, "cumulative base Value (mL/min)")
                ? this.toNumber(this.getVal(row, "cumulative base Value (mL/min)"))
                : this.toNumber(this.getVal(row, "Pump - Base Value (mL/min)"));
            integratedBase += (base_value_g_min ?? 0) * deltaMinutes;
            const methanol_value = this.toNumber(this.getVal(row, "Methanol feed (g/min)! Value (mL/min)"));
            integratedMethanol += (methanol_value ?? 0) * deltaMinutes;
            const nutrients_value_g_min = this.hasVal(row, "cumulative nutrients Value (mL/min)")
                ? this.toNumber(this.getVal(row, "cumulative nutrients Value (mL/min)"))
                : this.toNumber(this.getVal(row, "Nutrients Value (mL/min)"));
            integratedNutrients += (nutrients_value_g_min ?? 0) * deltaMinutes;
            // Map attributes to standardized format
            const attributes = this.mapAttributes(rawData);
            yield {
                fermentationId,
                timestamp_s,
                attributes,
            };
        }
    }
    // Public methods for direct access (used by tests)
    getTankNumbers() {
        return [0];
    }
    extractProjectInfo(fileContents) {
        const content = fileContents || this.fileContents;
        if (!content) {
            throw new Error("No file contents available");
        }
        const startTime = this.parseDateTime(this.extractStartTime(content));
        return { startTime };
    }
    getAttributeMapping() {
        return {
            // pH attributes
            ph_value: "ph_pv",
            ph_setpoint: "ph_sp",
            ph_output_pct: "ph_out",
            // Temperature attributes
            temperature_value: "temp_pv",
            temperature_setpoint: "temp_sp",
            temperature_output_pct: "temp_out",
            // Dissolved oxygen attributes
            do_value_pct: "do_pv",
            do_setpoint_pct: "do_sp",
            do_output_pct: "do_out",
            // Agitation attributes
            agitation_value_rpm: "agit_pv",
            agitation_setpoint_rpm: "agit_sp",
            // Air flow attributes
            air_flow_value_l_min: "airflow_pv",
            air_flow_setpoint_l_min: "airflow_sp",
            // Feed attributes
            antifoam_value_g_min: "antifoam_feed_rate_pv",
            antifoam_setpoint_g_min: "antifoam_feed_rate_sp",
            antifoam_total_g: "antifoam_feed_total", // Map legacy total_g to new added_g naming
            base_value_g_min: "base_feed_rate_pv",
            base_total_g: "base_feed_total", // Map legacy total_g to new added_g naming
            methanol_value_g_min: "methanol_feed_rate_pv",
            methanol_setpoint_g_min: "methanol_feed_rate_sp",
            methanol_total_g: "methanol_feed_total", // Map legacy total_g to new added_g naming
            nutrients_value_g_min: "nutrient_feed_rate_pv",
            nutrients_setpoint_g_min: "nutrient_feed_rate_sp",
            nutrients_total_g: "nutrient_feed_total", // Map legacy total_g to new added_g naming
        };
    }
    extractStartTime(fileContents) {
        const separator = fileContents.startsWith("Date;Time;") ? ";" : ",";
        const dateStartIdx = fileContents.indexOf("\n") + 1;
        const dateEndIdx = fileContents.indexOf(separator, dateStartIdx);
        const timeStartIdx = dateEndIdx + 1;
        const timeEndIdx = fileContents.indexOf(separator, timeStartIdx);
        const date = fileContents.slice(dateStartIdx, dateEndIdx);
        const time = fileContents.slice(timeStartIdx, timeEndIdx);
        return `${date} ${time}`;
    }
    parseDateTime(dateTimeStr) {
        return parse(dateTimeStr, this.dateTimeFormat, new Date(), {
            in: tz("Europe/Amsterdam"),
        });
    }
    createRawData(row, deltaMinutes, integrated) {
        // Antifoam
        const antifoam_value = this.toNumber(this.getVal(row, "Antifoam Value (V)"));
        const antifoam_total_g = this.hasVal(row, "cumulative antifoam Cumulative (mL)")
            ? this.toNumber(this.getVal(row, "cumulative antifoam Cumulative (mL)"))
            : integrated.integratedAntifoam;
        // Base
        const base_value_g_min = this.hasVal(row, "cumulative base Value (mL/min)")
            ? this.toNumber(this.getVal(row, "cumulative base Value (mL/min)"))
            : this.toNumber(this.getVal(row, "Pump - Base Value (mL/min)"));
        const base_total_g = this.hasVal(row, "cumulative base Cumulative (mL)")
            ? this.toNumber(this.getVal(row, "cumulative base Cumulative (mL)"))
            : integrated.integratedBase;
        // Methanol
        const methanol_value = this.toNumber(this.getVal(row, "Methanol feed (g/min)! Value (mL/min)"));
        const methanol_total_g = this.hasVal(row, "cumulative methanol Cumulative (mL)")
            ? this.toNumber(this.getVal(row, "cumulative methanol Cumulative (mL)"))
            : integrated.integratedMethanol;
        // Nutrients
        const nutrients_value_g_min = this.hasVal(row, "cumulative nutrients Value (mL/min)")
            ? this.toNumber(this.getVal(row, "cumulative nutrients Value (mL/min)"))
            : this.toNumber(this.getVal(row, "Nutrients Value (mL/min)"));
        const nutrients_total_g = this.hasVal(row, "cumulative nutrients Cumulative (mL)")
            ? this.toNumber(this.getVal(row, "cumulative nutrients Cumulative (mL)"))
            : integrated.integratedNutrients;
        return {
            ph_value: this.toNumber(this.getVal(row, "pH Value (pH)")),
            ph_setpoint: this.toNumber(this.getVal(row, "pH Setpoint (pH)")),
            ph_output_pct: this.toNumber(this.getVal(row, "pH Output")),
            temperature_value: this.toNumber(this.getVal(row, "Temperature Value (°C)")),
            temperature_setpoint: this.toNumber(this.getVal(row, "Temperature Setpoint (°C)")),
            temperature_output_pct: this.toNumber(this.getVal(row, "Temperature Output")),
            do_value_pct: this.toNumber(this.getVal(row, "pO? Value (pO?)")),
            do_setpoint_pct: this.toNumber(this.getVal(row, "pO? Setpoint (pO?)")),
            do_output_pct: this.toNumber(this.getVal(row, "pO? Output")),
            agitation_value_rpm: this.toNumber(this.getVal(row, "Stirrer Value (rpm)")),
            agitation_setpoint_rpm: this.toNumber(this.getVal(row, "Stirrer Setpoint (rpm)")),
            air_flow_value_l_min: this.toNumber(this.getVal(row, "Flow - Air Value (L/min)")),
            air_flow_setpoint_l_min: this.toNumber(this.getVal(row, "Flow - Air Setpoint (L/min)")),
            antifoam_value_g_min: antifoam_value,
            antifoam_setpoint_g_min: this.toNumber(this.getVal(row, "Antifoam Setpoint (V)")),
            antifoam_total_g: antifoam_total_g,
            base_value_g_min: base_value_g_min,
            base_total_g: base_total_g,
            methanol_value_g_min: this.toNumber(this.getVal(row, "Methanol feed (g/min)! Setpoint (mL/min)")),
            methanol_setpoint_g_min: this.toNumber(this.getVal(row, "Methanol feed (g/min)! Setpoint (mL/min)")),
            methanol_total_g: methanol_total_g,
            nutrients_value_g_min: nutrients_value_g_min,
            nutrients_setpoint_g_min: this.toNumber(this.getVal(row, "Nutrients Setpoint (mL/min)")),
            nutrients_total_g: nutrients_total_g,
        };
    }
    getVal(row, columnName) {
        const normalizeColumnName = (name) => name
            .toLowerCase()
            .replace(/[°\uFFFD]/g, "") // Remove degree symbols and replacement chars
            .replace(/[^\x20-\x7E]/g, "") // Remove all non-ASCII chars
            .replace(/\s+/g, " ") // Normalize whitespace
            .trim();
        const normalizedTargetName = normalizeColumnName(columnName);
        const key = Object.keys(row).find((k) => normalizeColumnName(k) === normalizedTargetName);
        return key ? row[key] : null;
    }
    hasVal(row, columnName) {
        const normalizeColumnName = (name) => name
            .toLowerCase()
            .replace(/[°\uFFFD]/g, "") // Remove degree symbols and replacement chars
            .replace(/[^\x20-\x7E]/g, "") // Remove all non-ASCII chars
            .replace(/\s+/g, " ") // Normalize whitespace
            .trim();
        const normalizedTargetName = normalizeColumnName(columnName);
        const key = Object.keys(row).find((k) => normalizeColumnName(k) === normalizedTargetName);
        return key ? true : false;
    }
    guessDateTimeFormat(dateTimeStr) {
        const commonFormats = [
            "yyyy-MM-dd HH:mm:ss",
            "yyyy-MM-dd HH.mm.ss",
            "dd-MM-yy hh:mm:ss a",
            "MMM dd, yyyy hh:mm:ss a",
            "MMMM d, yyyy hh:mm:ss a",
            "MMMM dd, yyyy HH:mm:ss",
            "dd/MM/yyyy HH:mm:ss",
            "MM/dd/yyyy HH:mm:ss",
            "yyyy.MM.dd HH:mm:ss",
            "dd.MM.yyyy HH:mm:ss",
        ];
        for (const format of commonFormats) {
            try {
                const date = parse(dateTimeStr, format, new Date());
                if (!isNaN(date.getTime())) {
                    return [format, date];
                }
            }
            catch {
                continue;
            }
        }
        throw new Error(`Unknown date format: ${dateTimeStr}`);
    }
    async parseCsv(content, delimiter = ";") {
        return new Promise((resolve, reject) => Papa.parse(content, {
            delimiter,
            header: true,
            complete: (result) => {
                resolve(result.data);
            },
            error: reject,
        }));
    }
}
//# sourceMappingURL=biostream.js.map